import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react'

interface UIContextType {
    sideNavExpanded: boolean
    showAnnouncements: boolean
    showFeedbackModal: boolean
    setSideNavExpanded: (expanded: boolean) => void
    setShowAnnouncements: (show: boolean) => void
    setShowFeedbackModal: (show: boolean) => void
    toggleAnnouncements: () => void
    toggleFeedbackModal: () => void
}

const UIContext = createContext<UIContextType | undefined>(undefined)

export const useUI = () => {
    const context = useContext(UIContext)
    if (context === undefined) {
        throw new Error('useUI must be used within a UIProvider')
    }
    return context
}

interface UIProviderProps {
    children: ReactNode
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
    const [sideNavExpanded, setSideNavExpanded] = useState(false)
    const [showAnnouncements, setShowAnnouncements] = useState(false)
    const [showFeedbackModal, setShowFeedbackModal] = useState(false)

    const toggleAnnouncements = useCallback(() => {
        setShowAnnouncements((prev) => !prev)
    }, [])

    const toggleFeedbackModal = useCallback(() => {
        setShowFeedbackModal((prev) => !prev)
    }, [])

    // Memoize the context value
    const value = useMemo<UIContextType>(
        () => ({
            sideNavExpanded,
            showAnnouncements,
            showFeedbackModal,
            setSideNavExpanded,
            setShowAnnouncements,
            setShowFeedbackModal,
            toggleAnnouncements,
            toggleFeedbackModal,
        }),
        [sideNavExpanded, showAnnouncements, showFeedbackModal, toggleAnnouncements, toggleFeedbackModal],
    )

    return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}
